// =============================================================================
// JEDYNE miejsce wywołań HTTP do print-core z powłoki. Każda zakładka woła
// stąd, bo autoryzacja jest jedna: Bearer z sesji Supabase + `x-organization-id`
// (userGate print-core weryfikuje członkostwo po swojej stronie — nagłówek jest
// tylko prośbą, nie źródłem prawdy).
//
// `VITE_PRINT_CORE_URL` może być PUSTE — print-core nie jest jeszcze wdrożony
// na Railway (plan, Faza 0). Wtedy każda akcja zapisu w UI jest disabled
// z tooltipem `KOMUNIKAT_BRAK_PRINT_CORE`, a odczyty przez RLS działają
// normalnie. ŻADNEJ zgadywanej domeny: adres pochodzi wyłącznie z env.
//
// Ścieżki są DOKŁADNIE kontraktem v2 print-core (typy drutu:
// `packages/types/src/print.ts`); istniejące trasy agentów wg
// `services/print-core/src/index.ts`.
// =============================================================================

import type { PrintJobRequestV2, PrintJobStatusOdpowiedz } from '@insolitum/types';

import { supabase } from '../../../lib/supabase';

/** Tooltip każdej wyłączonej akcji zapisu — jeden tekst, jedno źródło. */
export const KOMUNIKAT_BRAK_PRINT_CORE = 'print-core nie jest jeszcze wdrożony';

/**
 * Adres czytany PRZY KAŻDYM wywołaniu (funkcja, nie stała modułu), żeby testy
 * mogły go podstawić przez `vi.stubEnv` bez re-importu modułu.
 */
export function adresPrintCore(): string {
  const surowy = (import.meta.env.VITE_PRINT_CORE_URL as string | undefined) ?? '';
  return surowy.trim().replace(/\/+$/, '');
}

export function printCoreDostepny(): boolean {
  return adresPrintCore().length > 0;
}

/** Błąd toru print-core: status HTTP + kod `{error}` + komunikat `{message}`. */
export class PrintCoreError extends Error {
  constructor(
    public readonly status: number,
    public readonly kod: string,
    komunikat: string,
    /**
     * CAŁE ciało błędu, nie tylko `{error, message}`. Trasy układu odsyłają
     * w błędzie dane, bez których panel nie umie zareagować sensownie:
     * `current_version` przy 409 `version_conflict` (żeby pokazać „ktoś
     * opublikował wersję N" bez dodatkowego GET-a) oraz `errors` przy 422
     * (żeby podświetlić konkretny węzeł zamiast pokazać zdanie o całości).
     * Bez tego pola edytor musiałby albo dopytywać serwer, albo zgadywać.
     */
    public readonly dane: Record<string, unknown> = {}
  ) {
    super(komunikat);
    this.name = 'PrintCoreError';
  }
}

/** Co liczy limit planu druku — dopełniacz do zdania (print-core, `limityDruku.ts`). */
const OBIEKTY_LIMITU: Record<string, string> = {
  max_serwerow: 'serwerów wydruku',
  max_drukarek: 'aktywnych drukarek',
  wlasne_uklady: 'własnych układów wzorów',
};

/**
 * Odmowa limitu planu (402 `limit_planu`) jako zdanie dla administratora —
 * z liczb, które serwer odsyła w ciele, i ze wskazaniem, gdzie podnieść próg.
 * `null` = to nie jest odmowa limitu (wołający pokazuje własny komunikat).
 */
export function komunikatLimituPlanu(err: unknown): string | null {
  if (!(err instanceof PrintCoreError) || err.kod !== 'limit_planu') return null;
  const klucz = typeof err.dane.feature_key === 'string' ? err.dane.feature_key : '';
  const obiekt = OBIEKTY_LIMITU[klucz] ?? 'obiektów druku';
  const limit = typeof err.dane.limit === 'number' ? err.dane.limit : null;
  const biezace = typeof err.dane.current === 'number' ? err.dane.current : null;
  if (limit === 0) {
    return `Plan organizacji nie obejmuje ${obiekt}. Organ druku (albo PackFlow z torem druku) włączysz w Ustawieniach → Moduły.`;
  }
  const liczby = limit !== null && biezace !== null ? ` (${biezace}/${limit})` : '';
  return `Wykorzystano limit ${obiekt} w planie organizacji${liczby}. Wyższy próg organu druku wybierzesz w Ustawieniach → Moduły.`;
}

interface OpcjeWolania {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
}

async function naglowki(organizationId: string): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) {
    throw new PrintCoreError(401, 'brak_sesji', 'Brak aktywnej sesji użytkownika');
  }
  return {
    Authorization: `Bearer ${token}`,
    'x-organization-id': organizationId,
  };
}

async function wolaj<T>(
  organizationId: string,
  sciezka: string,
  opcje: OpcjeWolania = {}
): Promise<T> {
  const baza = adresPrintCore();
  if (!baza) {
    // Zapis bez wdrożonego print-core to błąd programisty UI (przyciski mają
    // być disabled) — ale bronimy się i tutaj, z tym samym komunikatem.
    throw new PrintCoreError(0, 'print_core_niedostepny', KOMUNIKAT_BRAK_PRINT_CORE);
  }
  const headers = await naglowki(organizationId);
  if (opcje.body !== undefined) headers['Content-Type'] = 'application/json';

  let odpowiedz: Response;
  try {
    odpowiedz = await fetch(`${baza}${sciezka}`, {
      method: opcje.method ?? 'GET',
      headers,
      body: opcje.body !== undefined ? JSON.stringify(opcje.body) : undefined,
    });
  } catch {
    throw new PrintCoreError(0, 'siec', 'Nie udało się połączyć z serwerem druku');
  }

  if (!odpowiedz.ok) {
    // Kontrakt błędów print-core: `{ error, message }` (handle() w index.ts).
    let kod = 'blad';
    let komunikat = `Błąd serwera druku (HTTP ${odpowiedz.status})`;
    let dane: Record<string, unknown> = {};
    try {
      const cialo = (await odpowiedz.json()) as {
        error?: string;
        message?: string;
        [k: string]: unknown;
      };
      if (cialo.error) kod = cialo.error;
      if (cialo.message) komunikat = cialo.message;
      dane = cialo;
    } catch {
      // Ciało nie było JSON-em — zostaje komunikat ogólny.
    }
    throw new PrintCoreError(odpowiedz.status, kod, komunikat, dane);
  }

  return (await odpowiedz.json()) as T;
}

// ── Zadania ──────────────────────────────────────────────────────────────────

export interface ZadanieUtworzone {
  id: string;
  status: string;
  idempotent_replay?: boolean;
}

/**
 * `POST /api/print/jobs` (kontrakt v2). Odpowiedź normalizujemy: trasa v1
 * odpowiada `{ job: {...} }`, v2 może odpowiedzieć płasko — czytamy oba
 * kształty, żeby UI nie zależał od kolejności wdrożeń.
 */
export async function utworzZadanie(
  organizationId: string,
  cialo: PrintJobRequestV2
): Promise<ZadanieUtworzone> {
  const dane = await wolaj<{ job?: ZadanieUtworzone } & Partial<ZadanieUtworzone>>(
    organizationId,
    '/api/print/jobs',
    { method: 'POST', body: cialo }
  );
  const zadanie = dane.job ?? (dane as ZadanieUtworzone);
  return {
    id: zadanie.id,
    status: zadanie.status,
    idempotent_replay: (dane as ZadanieUtworzone).idempotent_replay ?? zadanie.idempotent_replay,
  };
}

/** `GET /api/print/jobs/:id` — status dla pollerów. */
export async function pobierzZadanie(
  organizationId: string,
  id: string
): Promise<PrintJobStatusOdpowiedz> {
  const dane = await wolaj<{ job?: PrintJobStatusOdpowiedz } & Partial<PrintJobStatusOdpowiedz>>(
    organizationId,
    `/api/print/jobs/${id}`
  );
  return dane.job ?? (dane as PrintJobStatusOdpowiedz);
}

/** `POST /api/print/jobs/:id/cancel` — atomowo, wyłącznie ze stanu `queued`. */
export async function anulujZadanie(organizationId: string, id: string): Promise<void> {
  await wolaj(organizationId, `/api/print/jobs/${id}/cancel`, { method: 'POST' });
}

/** `POST /api/print/jobs/:id/reprint` — istniejąca trasa; klonuje bajty, nigdy re-render. */
export async function ponowZadanie(
  organizationId: string,
  id: string
): Promise<{ job: { id: string }; source_job_id: string }> {
  return wolaj(organizationId, `/api/print/jobs/${id}/reprint`, { method: 'POST' });
}

// ── Kartoteka drukarek (CRUD userGate+admin) ─────────────────────────────────

export interface CialoDrukarki {
  name?: string;
  type?: 'network' | 'system' | 'virtual';
  ip?: string | null;
  port?: number | null;
  protocol?: string | null;
  format?: string | null;
  system_name?: string | null;
  is_default?: boolean;
  is_active?: boolean;
}

export async function utworzDrukarke(
  organizationId: string,
  cialo: CialoDrukarki
): Promise<{ printer?: { id: string } }> {
  return wolaj(organizationId, '/api/print/printers', { method: 'POST', body: cialo });
}

export async function aktualizujDrukarke(
  organizationId: string,
  id: string,
  cialo: CialoDrukarki
): Promise<{ printer?: { id: string } }> {
  return wolaj(organizationId, `/api/print/printers/${id}`, { method: 'PATCH', body: cialo });
}

export async function usunDrukarke(organizationId: string, id: string): Promise<void> {
  await wolaj(organizationId, `/api/print/printers/${id}`, { method: 'DELETE' });
}

// ── Szablony (katalog + odstępstwa tenanta) ──────────────────────────────────

export interface SzablonKatalogu {
  slug: string;
  name: string;
  family?: string;
  organ?: string;
  trigger?: string;
  targets?: string[];
  variables?: Array<{ name: string; description?: string; example?: unknown; optional?: boolean }>;
  /** Czy tenant ma aktywne odstępstwo (chip „dostosowany"). */
  customized?: boolean;
  /** Liquid odstępstwa tenanta — jeśli serwer go zwraca (edytor). */
  /**
   * Treść odstępstwa Liquid — TYLKO dla administratora organizacji. Zwykły
   * członek dostaje `null`, bo lista jest dostępna dla każdego członka,
   * a treść odstępstwa jest materiałem administracyjnym.
   */
  override_liquid?: string | null;
  /** „Ten wzór ma odstępstwo Liquid" — flaga widoczna dla każdego członka. */
  has_liquid?: boolean;
}

/** `GET /api/print/templates` — katalog z kodu + odstępstwa tenanta. */
export async function pobierzSzablony(organizationId: string): Promise<SzablonKatalogu[]> {
  const dane = await wolaj<{ templates?: SzablonKatalogu[] } | SzablonKatalogu[]>(
    organizationId,
    '/api/print/templates'
  );
  return Array.isArray(dane) ? dane : (dane.templates ?? []);
}

export interface WynikRenderu {
  /** Adres artefaktu (podpisany URL) — gdy serwer odpowiada JSON-em. */
  url: string | null;
  /** Bajty renderu — gdy serwer streamuje PDF/PNG wprost. */
  blob: Blob | null;
  /** Tekst (np. surowy ZPL w karcie eksperckiej) — gdy odpowiedź tekstowa. */
  tekst: string | null;
  contentType: string;
}

/**
 * `POST /api/print/render {template_slug, variables?, target}` — podgląd jest
 * PRAWDZIWĄ ścieżką renderu (plan §5.5), więc UI tylko pokazuje bajty, których
 * kształt (PDF / obraz / JSON z URL-em / tekst) zależy od celu.
 *
 * `variables` POMINIĘTE = podgląd na danych przykładowych wzoru (serwer
 * podstawia `previewVars`). Jawne `{}` to „zero zmiennych" i dla wzoru z polem
 * nieopcjonalnym kończy się 422 `missing_variable` — to nie jest to samo.
 */
export async function renderujSzablon(
  organizationId: string,
  cialo: { template_slug: string; variables?: Record<string, unknown>; target: string }
): Promise<WynikRenderu> {
  const baza = adresPrintCore();
  if (!baza) {
    throw new PrintCoreError(0, 'print_core_niedostepny', KOMUNIKAT_BRAK_PRINT_CORE);
  }
  const headers = await naglowki(organizationId);
  headers['Content-Type'] = 'application/json';

  let odpowiedz: Response;
  try {
    odpowiedz = await fetch(`${baza}/api/print/render`, {
      method: 'POST',
      headers,
      body: JSON.stringify(cialo),
    });
  } catch {
    throw new PrintCoreError(0, 'siec', 'Nie udało się połączyć z serwerem druku');
  }
  return odczytajRender(odpowiedz, 'Render nie powiódł się');
}

/**
 * Wspólny odczyt odpowiedzi renderu dla `/api/print/render` i `…/podglad`.
 *
 * Wydzielone, bo obie trasy oddają TO SAMO: kształt odpowiedzi (PDF / obraz /
 * JSON z podpisanym URL-em / goły tekst ZPL) zależy od celu, nie od trasy.
 * Dwie kopie tej logiki rozjechałyby się przy pierwszym nowym celu — a rozjazd
 * objawiłby się jako „podgląd pokazuje co innego niż wydruk", czyli dokładnie
 * to, czego podgląd ma nie robić.
 */
async function odczytajRender(odpowiedz: Response, opisBledu: string): Promise<WynikRenderu> {
  if (!odpowiedz.ok) {
    let kod = 'blad';
    let komunikat = `${opisBledu} (HTTP ${odpowiedz.status})`;
    let dane: Record<string, unknown> = {};
    try {
      const cialoBledu = (await odpowiedz.json()) as {
        error?: string;
        message?: string;
        [k: string]: unknown;
      };
      if (cialoBledu.error) kod = cialoBledu.error;
      if (cialoBledu.message) komunikat = cialoBledu.message;
      dane = cialoBledu;
    } catch {
      // Nie-JSON — komunikat ogólny wystarczy.
    }
    throw new PrintCoreError(odpowiedz.status, kod, komunikat, dane);
  }

  const contentType = odpowiedz.headers.get('content-type') ?? '';
  if (contentType.includes('application/json')) {
    const dane = (await odpowiedz.json()) as { payload_url?: string; payload?: string };
    return {
      url: dane.payload_url ?? null,
      blob: null,
      tekst: dane.payload ?? null,
      contentType,
    };
  }
  if (contentType.startsWith('application/pdf') || contentType.startsWith('image/')) {
    return { url: null, blob: await odpowiedz.blob(), tekst: null, contentType };
  }
  return { url: null, blob: null, tekst: await odpowiedz.text(), contentType };
}

/** `PUT /api/print/templates/:slug/override` — odstępstwo tenanta. */
export async function zapiszOdstepstwo(
  organizationId: string,
  slug: string,
  cialo: { liquid: string }
): Promise<void> {
  await wolaj(organizationId, `/api/print/templates/${slug}/override`, {
    method: 'PUT',
    body: cialo,
  });
}

/** `DELETE /api/print/templates/:slug/override` — „Przywróć domyślny". */
export async function usunOdstepstwo(organizationId: string, slug: string): Promise<void> {
  await wolaj(organizationId, `/api/print/templates/${slug}/override`, { method: 'DELETE' });
}

// ── Układy tenanta (E2 — edytor wizualny) ────────────────────────────────────
//
// Trasy z E1. Odstępstwo tenanta ma DWIE wykluczające się postacie (`kind`):
// `liquid` (tylko PDF) albo `layout` (wszystkie cele). Serwer pilnuje, żeby
// nie mieszać ich w jednym wierszu — stąd 409 `override_kind_conflict`.
//
// SZKIC JEST KOLUMNĄ (`layout_draft`), nie osobnym wierszem: na wzór przypada
// dokładnie jeden wiersz (`UNIQUE(organization_id, template_key)`). Zapis
// szkicu NIE podbija `version` — ta liczy PUBLIKACJE, i to ją publikacja
// porównuje warunkowym UPDATE-em, żeby dwie karty przeglądarki nie nadpisały
// się po cichu.

/**
 * Błąd układu wskazujący WĘZEŁ, nie tylko ścieżkę.
 *
 * `nodeId` jest tym, po czym edytor zaznacza winowajcę: `sciezka`
 * (`nodes[3].zrodlo.wiersz[1]`) mówi, gdzie błąd siedzi w JSON-ie, ale nie
 * przeżywa przestawienia warstw — a to pierwsza rzecz, którą robi się po
 * przeczytaniu ostrzeżenia.
 */
export interface BladWezla {
  sciezka: string;
  kod: string;
  komunikat: string;
  nodeId?: string;
}

/** Odpowiedź `GET …/uklad` — stan edytora w chwili otwarcia. */
export interface StanUkladu {
  slug: string;
  kind: 'liquid' | 'layout' | null;
  /** Licznik PUBLIKACJI — to jego odsyła się jako `expected_version`. */
  version: number;
  is_active: boolean;
  layout: unknown | null;
  layout_draft: unknown | null;
  layout_version: number | null;
  base_template_version: number | null;
  published_at: string | null;
  catalog_version: number;
  /** Katalog odjechał od zasiewu — chip „wymaga przeglądu", nie błąd. */
  drift: boolean;
  /** Nieopcjonalne zmienne wzoru, których układ nie używa — bramka publikacji. */
  unbound_required: string[];
  /**
   * Bloki wyjeżdżające poza obszar zadruku. Bramka PUBLIKACJI, nie zapisu:
   * szkic z blokiem w pół drogi za krawędź zapisuje się normalnie, bo odmowa
   * autozapisu zabierałaby robotę, której nikt jeszcze nie skończył.
   * Starszy serwer tego pola nie zna — stąd `?`, a nie `?? []` po stronie
   * czytelnika: „nie podaje" i „nic nie wystaje" to dwa różne zdania.
   */
  out_of_bounds?: BladWezla[];
  errors: BladWezla[];
}

/** `GET /api/print/templates/:slug/uklad` — szkic, a gdy go nie ma: opublikowany. */
export async function pobierzUklad(organizationId: string, slug: string): Promise<StanUkladu> {
  return wolaj<StanUkladu>(organizationId, `/api/print/templates/${slug}/uklad`);
}

/** Odpowiedź `GET …/uklad/zasiew` — punkt startowy edytora dla wzoru bez układu. */
export interface ZasianyUklad {
  slug: string;
  layout: unknown;
  catalog_version: number;
  /** Zmienne, których zasiew nie umiał związać — do pokazania, nie do blokady. */
  unbound: string[];
  warnings: string[];
}

/**
 * `GET /api/print/templates/:slug/uklad/zasiew` — układ startowy z katalogu.
 *
 * Zasiew liczy SERWER, bo zasiew potrzebuje katalogu, a katalog żyje w kodzie
 * usługi. Wersja 422 `seed_not_faithful` znaczy „tego wzoru nie umiemy zasiać
 * bez zmiany wydruku" — edytor ma wtedy odmówić otwarcia, a nie pokazać układ,
 * który wydrukuje coś innego niż katalog.
 */
export async function zasiejUklad(organizationId: string, slug: string): Promise<ZasianyUklad> {
  return wolaj<ZasianyUklad>(organizationId, `/api/print/templates/${slug}/uklad/zasiew`);
}

/** Odpowiedź `PUT …/uklad/szkic`. */
export interface ZapisanySzkic {
  slug: string;
  kind: 'liquid' | 'layout' | null;
  version: number;
  has_draft: boolean;
  /**
   * Bloki wyjeżdżające poza obszar zadruku — liczone przy TYM zapisie, więc
   * edytor odświeża ostrzeżenie razem z autozapisem, a nie osobnym GET-em.
   * Opcjonalne, bo starszy serwer tego pola nie zna.
   */
  out_of_bounds?: BladWezla[];
}

/**
 * `PUT /api/print/templates/:slug/uklad/szkic` — autozapis pracy w toku.
 *
 * Szkic sprawdza WYŁĄCZNIE gramatykę. Niezwiązana zmienna nieopcjonalna nie
 * blokuje zapisu — blokuje dopiero publikację. Odwrotnie byłoby okrutne:
 * odmowa zapisu kasuje robotę, której użytkownik jeszcze nie skończył.
 */
export async function zapiszSzkicUkladu(
  organizationId: string,
  slug: string,
  layout: unknown
): Promise<ZapisanySzkic> {
  return wolaj<ZapisanySzkic>(organizationId, `/api/print/templates/${slug}/uklad/szkic`, {
    method: 'PUT',
    body: { layout },
  });
}

/**
 * `DELETE /api/print/templates/:slug/uklad/szkic` — świadome porzucenie szkicu.
 *
 * Jedyna droga, którą szkic ginie, i jedyne wyjście z sytuacji „zacząłem układ,
 * a chcę wrócić do odstępstwa Liquid": `DELETE …/override` filtruje po
 * `is_active`, a wiersz-szkic przed pierwszą publikacją jest nieaktywny.
 */
export async function porzucSzkicUkladu(organizationId: string, slug: string): Promise<void> {
  await wolaj(organizationId, `/api/print/templates/${slug}/uklad/szkic`, { method: 'DELETE' });
}

/** Odpowiedź `POST …/uklad/publikuj`. */
export interface OpublikowanyUklad {
  template: {
    slug: string;
    kind: 'liquid' | 'layout' | null;
    version: number;
    customized: boolean;
    published_at: string | null;
  };
}

/**
 * `POST /api/print/templates/:slug/uklad/publikuj`.
 *
 * `expectedVersion` to `version` z `GET …/uklad` W CHWILI WCZYTANIA. Serwer
 * robi warunkowy UPDATE i przy rozjeździe odsyła 409 `version_conflict`
 * z `current_version` w ciele (czytelne przez `PrintCoreError.dane`).
 * Bez `layout` publikuje zapisany szkic — panel nie musi odsyłać treści,
 * którą serwer już ma.
 */
export async function opublikujUklad(
  organizationId: string,
  slug: string,
  expectedVersion: number,
  layout?: unknown
): Promise<OpublikowanyUklad> {
  return wolaj<OpublikowanyUklad>(organizationId, `/api/print/templates/${slug}/uklad/publikuj`, {
    method: 'POST',
    body:
      layout === undefined
        ? { expected_version: expectedVersion }
        : { expected_version: expectedVersion, layout },
  });
}

/**
 * `POST /api/print/templates/:slug/podglad` — podgląd TĄ SAMĄ ścieżką renderu,
 * którą pojedzie prawdziwy wydruk.
 *
 * `layout` w ciele ma pierwszeństwo nad zapisanym stanem, więc płótno pokazuje
 * to, co użytkownik właśnie przesunął, a nie ostatni autozapis.
 */
export async function podgladUkladu(
  organizationId: string,
  slug: string,
  opcje: {
    target: string;
    zrodlo?: 'szkic' | 'opublikowany' | 'katalog';
    layout?: unknown;
    variables?: Record<string, unknown>;
  }
): Promise<WynikRenderu> {
  const baza = adresPrintCore();
  if (!baza) {
    throw new PrintCoreError(0, 'print_core_niedostepny', KOMUNIKAT_BRAK_PRINT_CORE);
  }
  const headers = await naglowki(organizationId);
  headers['Content-Type'] = 'application/json';

  const cialo: Record<string, unknown> = { target: opcje.target, zrodlo: opcje.zrodlo ?? 'szkic' };
  if (opcje.layout !== undefined) cialo.layout = opcje.layout;
  if (opcje.variables !== undefined) cialo.variables = opcje.variables;

  let odpowiedz: Response;
  try {
    odpowiedz = await fetch(`${baza}/api/print/templates/${slug}/podglad`, {
      method: 'POST',
      headers,
      body: JSON.stringify(cialo),
    });
  } catch {
    throw new PrintCoreError(0, 'siec', 'Nie udało się połączyć z serwerem druku');
  }
  return odczytajRender(odpowiedz, 'Podgląd nie powiódł się');
}

// ── Przypisania (purpose × module → drukarka) ────────────────────────────────

export interface KluczPrzypisania {
  purpose: string;
  /** `null` = wiersz „(wszystkie)" — dziedziczony przez moduły bez własnego. */
  module: string | null;
  site_id: string | null;
}

/** `PUT /api/print/assignments` — upsert po kluczu (org, purpose, module, site). */
export async function zapiszPrzypisanie(
  organizationId: string,
  cialo: KluczPrzypisania & { printer_id: string }
): Promise<void> {
  await wolaj(organizationId, '/api/print/assignments', { method: 'PUT', body: cialo });
}

/** `DELETE /api/print/assignments` — klucz w ciele JSON (trasa bez `:id`). */
export async function usunPrzypisanie(
  organizationId: string,
  cialo: KluczPrzypisania
): Promise<void> {
  await wolaj(organizationId, '/api/print/assignments', { method: 'DELETE', body: cialo });
}

// ── Serwery wydruku (istniejące trasy agentów) ───────────────────────────────

export interface ZarejestrowanySerwer {
  agent: {
    id: string;
    name: string;
    status: string;
  };
}

/**
 * `POST /api/print/agents` — krok 1 enrollmentu (userGate + admin org).
 * Zwraca sam wiersz; kod parowania wystawia {@link kodParowaniaSerwera}.
 */
export async function zarejestrujSerwerDruku(
  organizationId: string,
  name: string
): Promise<ZarejestrowanySerwer> {
  return wolaj(organizationId, '/api/print/agents', { method: 'POST', body: { name } });
}

/** Powody odmowy z `print_agent_nowy_kod_parowania` — przepisane z migracji 629. */
const POWODY_KODU = ['agent_not_found', 'agent_revoked', 'forbidden', 'code_collision'] as const;

/** Sześciocyfrowy kod parowania serwera wydruku (RPC z migracji 629). */
export interface KodParowaniaSerwera {
  code: string;
  expires_at: string;
  agent_id: string;
  agent_name: string;
}

/**
 * Krok 2 enrollmentu — kod wystawia BAZA, nie print-core.
 *
 * Wzorzec jest ten sam co przy tablecie (`create_device_pairing_code`,
 * DevicesPage): funkcja jest SECURITY DEFINER i sama sprawdza członkostwo oraz
 * rolę po `auth.uid()`, więc panel woła ją kluczem zalogowanego administratora.
 * Kod jawny istnieje wyłącznie w tej odpowiedzi — w bazie zostaje bcrypt.
 */
export async function kodParowaniaSerwera(
  agentId: string,
  ttlMinut = 15
): Promise<KodParowaniaSerwera> {
  const { data, error } = await supabase.rpc('print_agent_nowy_kod_parowania', {
    p_agent_id: agentId,
    p_ttl_minutes: ttlMinut,
  });
  if (error) {
    // Funkcja sygnalizuje powody przez `RAISE EXCEPTION '<kod>'` i dokłada
    // czytelny HINT. PostgREST podaje surowy kod w `message`, a HINT w `hint` —
    // więc pokazujemy HINT, a `kod` zostawiamy do rozróżniania przypadków.
    const powod = POWODY_KODU.find((p) => error.message.includes(p)) ?? 'pairing_code_failed';
    const hint = (error as { hint?: string | null }).hint;
    throw new PrintCoreError(
      powod === 'forbidden' ? 403 : 400,
      powod,
      hint || 'Nie udało się wygenerować kodu parowania.'
    );
  }
  return data as KodParowaniaSerwera;
}

/** `POST /api/print/agents/:id/revoke` — natychmiastowe odcięcie serwera. */
export async function odwolajSerwerDruku(organizationId: string, id: string): Promise<void> {
  await wolaj(organizationId, `/api/print/agents/${id}/revoke`, { method: 'POST' });
}

// ── Zasoby graficzne tenanta (E6) ────────────────────────────────────────────

/** Wiersz kartoteki `print_assets` w kształcie, jakim odpowiada print-core. */
export interface ZasobDruku {
  id: string;
  sha256: string;
  mime: string;
  bytes: number;
  width_px: number;
  height_px: number;
  nazwa: string;
  created_at: string;
}

/** `GET /api/print/assets` — obrazy tej organizacji, najnowsze pierwsze. */
export async function pobierzZasoby(organizationId: string): Promise<ZasobDruku[]> {
  const dane = await wolaj<{ assets?: ZasobDruku[] }>(organizationId, '/api/print/assets');
  return dane.assets ?? [];
}

/**
 * `POST /api/print/assets` — wgranie obrazu.
 *
 * Base64 w JSON-ie, nie multipart: przy suficie 2 MiB narzut kodowania (+33%)
 * jest tańszy niż druga droga wejścia do usługi, którą trzeba osobno bramkować
 * i osobno pamiętać przy CORS-ie. `duplikat: true` znaczy „ten plik już tu
 * jest" i NIE jest błędem — serwer oddaje istniejący wiersz.
 */
export async function wgrajZasob(
  organizationId: string,
  plik: { nazwa: string; mime: string; base64: string }
): Promise<{ asset: ZasobDruku; duplikat: boolean }> {
  return wolaj<{ asset: ZasobDruku; duplikat: boolean }>(organizationId, '/api/print/assets', {
    method: 'POST',
    body: { nazwa: plik.nazwa, mime: plik.mime, data: plik.base64 },
  });
}

/** `DELETE /api/print/assets/:id` — usuwa wiersz i bajty. */
export async function usunZasob(organizationId: string, id: string): Promise<void> {
  await wolaj<void>(organizationId, `/api/print/assets/${id}`, { method: 'DELETE' });
}

/**
 * `File` → base64 BEZ prefiksu `data:`.
 *
 * Serwer chce same bajty: prefiks przeszedłby przez `Buffer.from(..., 'base64')`
 * po cichu (nielegalne znaki są ignorowane), dając plik krótszy o kilka bajtów
 * i inny skrót — czyli duplikat, którego deduplikacja nie rozpozna.
 */
export function plikDoBase64(plik: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const czytnik = new FileReader();
    czytnik.onerror = () => reject(new Error('Nie udało się odczytać pliku'));
    czytnik.onload = () => {
      const wynik = String(czytnik.result ?? '');
      const przecinek = wynik.indexOf(',');
      resolve(przecinek >= 0 ? wynik.slice(przecinek + 1) : wynik);
    };
    czytnik.readAsDataURL(plik);
  });
}
